/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from '@scuf/localization';
import PortfolioEnergyBottomTableWidget from './PortfolioEnergyBottomTableWidget';
import { Card } from '@scuf/common';
import descriptors from '../../../../../../locales/en-US/descriptors';
import {
  IDetailComponentProps,
  ISiteCard
} from '../../../../../DashboardKPIWidgets/IEnergyManagementProps';
import ConnectionError from '../../../../../shared/ErrorComponents/ConnectionError';
import withErrorStatus from '../../../../../shared/StatusWrappers/ErrorStatus';
import withLoadingStatus from '../../../../../shared/StatusWrappers/LoadingStatus';
import { isObjectOrArrayAndEmpty } from '../../../../../shared/Utils/Utils';
import { GetTableHeader } from '../../../../../shared/Utils/PortfolioEnergy/PortfolioEnergyVDUtils';
import { IPortfolioEnergyData } from '../../IPortfolioEnergyViewDetails';

interface IPortfolioEnergy extends ISiteCard {
  data: IPortfolioEnergyData;
  tabName: string;
}

const PortfolioEnergyBottomWidgetHOCDetil = (props: IPortfolioEnergy) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh, tabName } = props;
  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }
  return (
    <div>
      <PortfolioEnergyBottomTableWidget data={data} tabName={tabName} />
    </div>
  );
};

const TotalEmissionsSavingsByMeterComponentWithLoader = withLoadingStatus(
  withErrorStatus(PortfolioEnergyBottomWidgetHOCDetil)
);

const PortfolioEnergyBottomWidgetHOC = (props: IDetailComponentProps) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const { dataConnector, tabName } = props;

  const onRefresh = () =>
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();
  const { all, loading, error, status } = dataConnector.data;
  const carbonTotalSavingsData = (all as unknown) as IPortfolioEnergyData;
  const isDataAvailable = carbonTotalSavingsData ? true : false;

  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState({} as IPortfolioEnergyData);

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        setData(carbonTotalSavingsData);
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status]);
  const tableHeader = GetTableHeader(tabName);
  return (
    <Card className="Savemeter">
      <Card.Header className="emaas-carbon-view-details-total-emissions-savings-meter-card-header">
        <div className="emaas-site-count-header">
          <div>{t(tableHeader, { count: data?.breakdownBySite?.length })}</div>
          <div>{t(d.SITEWITHDATA)}</div>
        </div>
      </Card.Header>
      <Card.Content>
        <div className="meter-savings-Datatable detail-view">
          <TotalEmissionsSavingsByMeterComponentWithLoader
            loading={isLoading}
            error={errorInfo}
            statusCode={statusCode}
            onRefresh={onRefresh}
            data={data}
            loadingText={t(d.LOADING)}
            title={t(d.CONNECTIONERRORTITLE)}
            noDataMessage={t(d.NODATAAVAILABLE)}
            connectionErrorMessage={t(d.CONNECTIONERROR)}
            tabName={tabName!}
          />
        </div>
      </Card.Content>
    </Card>
  );
};
export default PortfolioEnergyBottomWidgetHOC;
